import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type GlobalState = {
  reloader: boolean;
  setReloader: (value: boolean) => void;
  showPropertiesSelector: number;
  setShowPropertiesSelector: (value: number) => void;
  topNotificationsBadge: number;
  setTopNotificationsBadge: (value: number) => void;
  pendingAuth: number;
  setPendingAuth: (value: number) => void;
  pendingGuests: number;
  setPendingGuests: (value: number) => void;
  guests: number;
  setGuests: (value: number) => void;    
  notifications: number;
  setNotifications: (value: number) => void;    
  idPropiedad: number;
  setIdPropiedad: (value: number) => void;
  propiedad: string;
  setPropiedad: (value: string) => void;
  idControl: number;
  setIdControl: (value: number) => void;
  JWT: string;
  setJWT: (value: string) => void;
  movil: string;
  setMovil: (value: string) => void;
  invoices: number;
  setInvoices: (value: number) => void;
  invoicesExpired: number;
  setInvoicesExpired: (value: number) => void;
  displayName: string;
  setDisplayName: (value: string) => void;
  returnUrl: string;
  setReturnUrl: (value: string) => void;
  handleValidateStatus2: () => Promise<void>;
  currentVersion: number;
  setCurrentVersion: (value: number) => void;
  serverVersion: number;
  setServerVersion: (value: number) => void;
  gData: Record<string, any>;
  setGData: (value: Record<string, any>) => void;
  menu: Record<string, any>;
  setMenu: (value: Record<string, any>) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;
};

const GlobalStateContext = createContext<GlobalState | undefined>(undefined);

type GlobalStateProviderProps = {
  children: React.ReactNode;
};

export const GlobalStateProvider = ({ children }: GlobalStateProviderProps) => {
  //const [currentVersion, setCurrentVersionState] = useState<number>(() => 128);
  const [reloader, setReloaderState] = useState<boolean>(() => localStorage.getItem('reloader') === 'true');
  const [showPropertiesSelector, setShowPropertiesSelectorState] = useState<number>(() => Number(localStorage.getItem('showPropertiesSelector')) || 0);
  const [topNotificationsBadge, setTopNotificationsBadgeState] = useState<number>(() => Number(localStorage.getItem('topNotificationsBadge')) || 0);
  const [pendingAuth, setPendingAuthState] = useState<number>(() => Number(localStorage.getItem('pendingAuth')) || 0);

  const [pendingGuests, setPendingGuestsState] = useState<number>(() => Number(localStorage.getItem('pendingGuests')) || 0);
  const [guests, setGuestsState] = useState<number>(() => Number(localStorage.getItem('guests')) || 0);
  const [notifications, setNotificationsState] = useState<number>(() => Number(localStorage.getItem('notifications')) || 0);
  
  const [idPropiedad, setIdPropiedadState] = useState<number>(() => Number(localStorage.getItem('idPropiedad')) || 0);
  const [propiedad, setPropiedadState] = useState<string>(() => localStorage.getItem('propiedad') || '');
  const [idControl, setIdControlState] = useState<number>(() => Number(localStorage.getItem('idControl')) || 0);
  const [currentVersion, setCurrentVersionState] = useState<number>(() => Number(localStorage.getItem('currentVersion')) || 129);
  const [JWT, setJWTState] = useState<string>(() => localStorage.getItem('JWT') || '');
  const [movil, setMovilState] = useState<string>(() => localStorage.getItem('movil') || '');
  const [invoices, setInvoicesState] = useState<number>(() => Number(localStorage.getItem('invoices')) || 0);
  const [invoicesExpired, setInvoicesExpiredState] = useState<number>(() => Number(localStorage.getItem('invoicesExpired')) || 0);
  const [displayName, setDisplayNameState] = useState<string>(() => localStorage.getItem('displayName') || '');
  const [returnUrl, setReturnUrlState] = useState<string>(() => localStorage.getItem('returnUrl') || '');
  const [serverVersion, setServerVersionState] = useState<number>(() => Number(localStorage.getItem('serverVersion')) || 0); 
  const [gData, setGDataState] = useState<Record<string, any>>(() => {
    try {
      const saved = localStorage.getItem('gData');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Failed to parse gData from localStorage:", error);
      localStorage.removeItem('gData');
      return {}; // ✅ Fix: ensure fallback return
    }
  });

  const [menu, setMenuState] = useState<Record<string, any>>(() => {
    try {
      const saved = localStorage.getItem('menu');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Failed to parse menu from localStorage:", error);
      localStorage.removeItem('menu');
      return {}; // ✅ Fix: ensure fallback return
    }
  });




  const [loading, setLoadingState] = useState<boolean>(false);
  const setLoading = (value: boolean) => setLoadingState(value);
  const setReloader = (value: boolean) => setReloaderState(value);
  const setShowPropertiesSelector = (value: number) => setShowPropertiesSelectorState(value);
  const setTopNotificationsBadge = (value: number) => setTopNotificationsBadgeState(value);
  const setPendingAuth = (value: number) => setPendingAuthState(value);
  const setPendingGuests = (value: number) => setPendingGuestsState(value);
  const setGuests = (value: number) => setGuestsState(value);
  const setNotifications = (value: number) => setNotificationsState(value);
  const setIdPropiedad = (value: number) => setIdPropiedadState(value);
  const setPropiedad = (value: string) => setPropiedadState(value);
  const setIdControl = (value: number) => setIdControlState(value);
  const setJWT = (value: string) => setJWTState(value);
  const setMovil = (value: string) => setMovilState(value);
  const setInvoices = (value: number) => setInvoicesState(value);
  const setInvoicesExpired = (value: number) => setInvoicesExpiredState(value);
  const setDisplayName = (value: string) => setDisplayNameState(value);
  const setReturnUrl = (value: string) => setReturnUrlState(value);
  const setCurrentVersion = (value: number) => setCurrentVersionState(value);
  const setServerVersion = (value: number) => setServerVersionState(value);
  const setGData = (value: Record<string, any>) => setGDataState(value);
  const setMenu = (value: Record<string, any>) => setMenuState(value);

  const handleValidateStatus2 = useCallback(async () => {
    // ... async logic ...
  }, [JWT, idPropiedad]);

  // Local storage syncing
  

  useEffect(() => { localStorage.setItem('reloader', reloader.toString()); }, [reloader]);
  useEffect(() => { localStorage.setItem('showPropertiesSelector', showPropertiesSelector.toString()); }, [showPropertiesSelector]);
  useEffect(() => { localStorage.setItem('topNotificationsBadge', topNotificationsBadge.toString()); }, [topNotificationsBadge]);
  useEffect(() => { localStorage.setItem('pendingAuth', pendingAuth.toString()); }, [pendingAuth]);
  useEffect(() => { localStorage.setItem('setPendingGuests', pendingGuests.toString()); }, [pendingGuests]);
  useEffect(() => { localStorage.setItem('setGuests', guests.toString()); }, [pendingGuests]);
  useEffect(() => { localStorage.setItem('setNotifications', notifications.toString()); }, [notifications]);

  useEffect(() => { localStorage.setItem('idPropiedad', idPropiedad.toString()); }, [idPropiedad]);
  useEffect(() => { localStorage.setItem('propiedad', propiedad); }, [propiedad]);
  useEffect(() => { localStorage.setItem('idControl', idControl.toString()); }, [idControl]);
  useEffect(() => { localStorage.setItem('JWT', JWT); }, [JWT]);
  useEffect(() => { localStorage.setItem('movil', movil); }, [movil]);
  useEffect(() => { localStorage.setItem('invoices', invoices.toString()); }, [invoices]);
  useEffect(() => { localStorage.setItem('invoicesExpired', invoicesExpired.toString()); }, [invoicesExpired]);
  useEffect(() => { localStorage.setItem('displayName', displayName); }, [displayName]);
  useEffect(() => { localStorage.setItem('returnUrl', returnUrl); }, [returnUrl]);
  useEffect(() => { localStorage.setItem('currentVersion', currentVersion.toString()); }, [currentVersion]);
  useEffect(() => { localStorage.setItem('serverVersion', serverVersion.toString()); }, [serverVersion]);
  useEffect(() => { localStorage.setItem('gData', JSON.stringify(gData)); }, [gData]);
  useEffect(() => { localStorage.setItem('menu', JSON.stringify(menu)); }, [menu]);

  useEffect(() => {
    const syncFromLocalStorage = (event: StorageEvent) => {
      if (event.key === 'idPropiedad') setIdPropiedadState(Number(event.newValue) || 0);
      if (event.key === 'propiedad') setPropiedadState(event.newValue || '');
      if (event.key === 'JWT') setJWTState(event.newValue || '');
      if (event.key === 'displayName') setDisplayNameState(event.newValue || '');
      if (event.key === 'gData') {
        try {
          setGDataState(event.newValue ? JSON.parse(event.newValue) : {});
        } catch (e) {
          console.error('Failed to parse gData from storage event:', e);
        }
      }
      if (event.key === 'menu') {
        try {
          setMenuState(event.newValue ? JSON.parse(event.newValue) : {});
        } catch (e) {
          console.error('Failed to parse menu from storage event:', e);
        }
      }
    };
    window.addEventListener('storage', syncFromLocalStorage);
    return () => window.removeEventListener('storage', syncFromLocalStorage);
  }, []);

  return (
    <GlobalStateContext.Provider
      value={{
        reloader, setReloader,
        showPropertiesSelector, setShowPropertiesSelector,
        topNotificationsBadge, setTopNotificationsBadge,
        pendingAuth, setPendingAuth,
        pendingGuests, setPendingGuests,
        guests, setGuests,
        notifications, setNotifications,
        idPropiedad, setIdPropiedad,
        propiedad, setPropiedad,
        idControl, setIdControl,
        JWT, setJWT,
        movil, setMovil,
        invoices, setInvoices,
        invoicesExpired, setInvoicesExpired,
        displayName, setDisplayName,
        returnUrl, setReturnUrl,
        handleValidateStatus2,
        currentVersion, setCurrentVersion,
        serverVersion, setServerVersion,
        gData, setGData,
        menu, setMenu,
        loading, setLoading, // ✅ Include loading in context
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (!context) throw new Error('useGlobalState must be used within GlobalStateProvider');
  return context;
};
